yarn<template>
  <LoadingSpinner v-if="loadingPurchaseOrder || projectLoading || vendorsLoading" />

  <div v-else class="edit-purchase-order">
    <PageHeader title="Edit Purchase Order" />

    <div class="mx-auto max-w-7xl">
      <PurchaseOrderUpdateForm
        :model="purchaseOrder"
        :project="project"
        :vendors="vendors"
        :save-function="updatePurchaseOrder"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import PurchaseOrderUpdateForm from '../components/PurchaseOrderUpdateForm.vue'
import { PurchaseOrder } from '@/models/purchaseOrder'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import PageHeader from '@/components/app/AppPageHeader.vue'
import { useHead } from '@unhead/vue'
import PurchaseOrderService from '@/services/PurchaseOrderService'
import VendorService from '@/services/VendorService'
import ProjectService from '@/services/ProjectService'
import BudgetService from '@/services/BudgetService'
import { useRouter, useRoute } from 'vue-router'
import { useNotification } from '@kyvg/vue3-notification'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { computed } from 'vue'


useHead({ title: 'Edit Purchase Order' })

const $router = useRouter()
const $route = useRoute()
const { notify } = useNotification()
const queryClient = useQueryClient()
const projectService = new ProjectService()
const vendorService = new VendorService()
const budgetService = new BudgetService()
const purchaseOrderId = $route.params.id as string || ''
const purchaseOrderService = new PurchaseOrderService('')

const { data: purchaseOrder, isLoading: loadingPurchaseOrder } = useQuery({
  queryKey: ['purchaseOrder', purchaseOrderId],
  queryFn: () => purchaseOrderService.find(purchaseOrderId)
})

const enbleProjectQuery = computed(() => purchaseOrder.value?.projectId !== '')

const { data: project, isLoading: projectLoading } = useQuery({
  queryKey: ['project', purchaseOrder.value?.projectId],
  queryFn: () => projectService.find(purchaseOrder.value?.projectId),
  enabled: enbleProjectQuery
})

const { data: vendors, isLoading: vendorsLoading } = useQuery({
  queryKey: ['vendors'],
  queryFn: () => vendorService.all().then((vendors) => vendors.map((vendor) => ({ value: vendor.id, label: vendor.name })))
})


const { mutateAsync: updatePurchaseOrder } = useMutation({
  mutationFn: (purchaseOrder: PurchaseOrder) => budgetService.updatePurchaseOrder(purchaseOrder.budgetId, purchaseOrder.id, purchaseOrder),
  onSuccess: (updatedPurchaseOrder: PurchaseOrder) => {
    queryClient.setQueryData(['purchaseOrder', updatedPurchaseOrder.id], updatedPurchaseOrder)
    queryClient.invalidateQueries({ queryKey: ['purchaseOrders'] })
    notify({ type: 'success', text: 'Purchase order updated successfully' })
    $router.push({ name: 'ShowPurchaseOrder', params: { id: updatedPurchaseOrder.id } })
  },
  onError: (err) => {
    notify({
      type: 'error',
      title: 'Error',
      text: err.message || 'An error occurred while trying to update the purchase order'
    })
  }
})

</script>
